import {
  CSSProperties,
  ReactNode,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
} from "react";
import cx from "classnames";
import { useUnit } from "effector-react";

import {
  useCameraViewOffset,
  CANVAS_SIDEBAR_GAP,
  CHAT_SIDEBAR_WIDTH,
  SIDEBAR_TRANSITION_DURATION,
} from "../../context/CameraViewOffsetContext";
import { $safeAreaInsets } from "../../state/safeArea";

import styles from "./AssistantChatSidebar.module.css";

type AssistantChatSidebarProps = {
  children: ReactNode;
  footer: ReactNode;
  afterFooter?: ReactNode;
  afterFooterHeight?: number;
  pinnedHeader?: ReactNode;
  pinnedBarHeight?: number;
  style?: CSSProperties;
  /**
   * Play the slide-in animation on mount. Set true for first-time HUD
   * appearance after splat reveal; false on snapshot-restore / cross-
   * breakpoint resize, when an instant appearance is desired.
   */
  animateInitialOffset?: boolean;
  /** When true, hidden via `display:none` (preserves DOM & scroll). */
  hidden?: boolean;
};

/**
 * Desktop chat sidebar — fixed-width right panel, always visible (no
 * drag-modes, no keyboard hacks). Slides in from the right on mount
 * and writes layout CSS-vars on `#app` so the canvas / overlays / top-
 * right buttons reflow synchronously. Camera principal point shifts
 * via `applyLayoutOffset({ right })` so the focal point stays roughly
 * centred in the visible canvas slice.
 *
 * Mirrors the mobile `AssistantChatPanel` layout primitives
 * (pinned header / wrapper / footer / afterFooter) so the same chat
 * `children` (history scroll, suggestions, chips, input) render
 * identically in both layouts.
 */
export function AssistantChatSidebar({
  children,
  footer,
  afterFooter,
  afterFooterHeight = 0,
  pinnedHeader,
  pinnedBarHeight = 0,
  style: externalStyle,
  animateInitialOffset,
  hidden,
}: AssistantChatSidebarProps) {
  const safeArea = useUnit($safeAreaInsets);
  const { applyLayoutOffset } = useCameraViewOffset();

  // Mount-ready gate — keeps `.entering` class on the first paint so
  // the slide-in transition has a starting state to animate from.
  // Double rAF: first frame lets React commit the entering class to
  // the DOM, second flips to the final state so the browser sees a
  // genuine prop change → fires the CSS transition.
  const [isMountReady, setIsMountReady] = useState(!animateInitialOffset);
  useEffect(() => {
    if (!animateInitialOffset) return;
    let id = requestAnimationFrame(() => {
      id = requestAnimationFrame(() => setIsMountReady(true));
    });
    return () => cancelAnimationFrame(id);
  }, [animateInitialOffset]);

  // CSS-var bridge: tells canvas / overlay / top-right-buttons that a
  // sidebar is reserved on the inline-end edge. Cleanup on unmount
  // (e.g. cross-breakpoint resize to mobile) restores defaults so the
  // mobile panel can take over without a stale right-side gap.
  //
  // `--panel-transition-duration` is intentionally shared with the
  // mobile panel's writer — they never coexist (mutually exclusive
  // layout modes). The duration drives canvas `inset-inline-end` and
  // `bottom` transitions, so writing it here keeps the canvas reflow
  // in sync with our slide-in.
  useLayoutEffect(() => {
    const root = document.getElementById("app");
    if (!root) return;
    const transition = animateInitialOffset
      ? `${SIDEBAR_TRANSITION_DURATION}ms`
      : "0ms";
    root.style.setProperty("--chat-sidebar-width", `${CHAT_SIDEBAR_WIDTH}px`);
    root.style.setProperty("--canvas-side-gap", `${CANVAS_SIDEBAR_GAP}px`);
    // Zero the mobile-panel vars — without these the canvas / overlays
    // would inherit `var(--panel-current-height, var(--panel-default-height))`
    // (= 106 px) from spacing.css :root defaults and leave a phantom
    // 138 px bottom gap reserved for a non-existent mobile chat panel.
    root.style.setProperty("--panel-current-height", "0px");
    root.style.setProperty("--canvas-panel-gap", "0px");
    root.style.setProperty("--panel-transition-duration", transition);
    return () => {
      root.style.removeProperty("--chat-sidebar-width");
      root.style.removeProperty("--canvas-side-gap");
      root.style.removeProperty("--panel-current-height");
      root.style.removeProperty("--canvas-panel-gap");
      root.style.removeProperty("--panel-transition-duration");
    };
  }, [animateInitialOffset]);

  // Camera principal-point shift. Canvas is force-`direction: ltr` in
  // `mainRenderer.ts`, so canvas-pixel-0 is at container's physical
  // LEFT in BOTH LTR and RTL — canvas always overflows on the physical
  // RIGHT regardless of writing direction. The CSS sidebar position
  // flips between physical sides via `inset-inline-end`, but the
  // camera math operates in canvas-pixel space and stays right-clipped.
  // So `viewOffset.right = sideValue` in both LTR and RTL; NEVER write
  // to `.left`.
  const animateInitialOffsetRef = useRef(animateInitialOffset);
  useEffect(() => {
    const sideValue = CHAT_SIDEBAR_WIDTH + CANVAS_SIDEBAR_GAP;
    const shouldAnimate = animateInitialOffsetRef.current ?? false;
    animateInitialOffsetRef.current = false;
    applyLayoutOffset(
      {
        top: safeArea.top,
        right: sideValue,
        bottom: 0,
        left: 0,
      },
      shouldAnimate,
      shouldAnimate ? SIDEBAR_TRANSITION_DURATION : undefined,
    );
    return () => {
      // Cleanup: reset to no-sidebar state. The next mounted layout
      // (mobile panel or another instance of the sidebar) writes its
      // own offset on top of this. Animated so the canvas / camera
      // smoothly tracks the layout change on cross-breakpoint resize.
      applyLayoutOffset(
        { top: safeArea.top, right: 0, bottom: 0, left: 0 },
        true,
        SIDEBAR_TRANSITION_DURATION,
      );
    };
  }, [applyLayoutOffset, safeArea.top]);

  return (
    <div
      className={cx(styles.sidebar, !isMountReady && styles.entering)}
      style={
        {
          display: hidden ? "none" : undefined,
          "--after-footer-height": `${afterFooterHeight}px`,
          ...externalStyle,
        } as CSSProperties
      }
    >
      {pinnedHeader}
      <div
        className={cx(styles.wrapper, pinnedBarHeight > 0 && styles.wrapperPinned)}
        style={{ "--pinned-bar-height": `${pinnedBarHeight}px` } as CSSProperties}
      >
        {children}
      </div>
      {footer}
      {afterFooter && <div className={styles.afterFooter}>{afterFooter}</div>}
    </div>
  );
}
